import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentArrowUpIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Badge, Button, Card, CardHeader, Input, Modal, ModalFooter, Select } from '@/components/common';
import { getErrorMessage } from '@/services';
import { formatDateTime, titleCase } from '@/utils';
import {
  CLAIM_DOCUMENT_TYPES,
  SERVER_CHECKLIST_CLAIM_TYPES,
  UPLOAD_ACCEPT_ATTRIBUTE,
  buildChecklist,
  claimChecklistApi,
  claimDocumentsApi,
  documentTypeLabel,
  formatFileSize,
  validateClaimDocumentFile,
  type ChecklistEntry,
  type ClaimDocumentRow,
  type ServerChecklist,
  type ServerChecklistItem,
} from './claimDocuments';

/**
 * Board #215 — the claim documents panel.
 *
 * The old panel was four lines of read-only markup with three defects: it never
 * rendered anything (it called `.length` on the paginated envelope), its "View"
 * link pointed at `file_path`, which is a storage path and not a URL, and there
 * was no way to upload at all — on a claim whose status is literally
 * "Documents Pending".
 *
 * Everything here is built against the shipped backend:
 * `POST   /claims/{id}/documents/`                  multipart upload
 * `GET    /claims/{id}/documents/{doc}/download/`   authenticated stream
 * `POST   /claims/{id}/documents/{doc}/verify/`     broker-side approve
 * `POST   /claims/{id}/documents/{doc}/reject/`     broker-side, reason required
 * `DELETE /claims/{id}/documents/{doc}/`            row + bytes
 */

/** Statuses where the claim is finished and new evidence is not expected. */
const CLOSED_CLAIM_STATUSES = ['settled', 'closed', 'rejected', 'withdrawn'];

/** The status the board item names: "Upload Document when status is Document Pending". */
const DOCUMENTS_PENDING_STATUS = 'documents_pending';

interface ClaimDocumentsCardProps {
  claimId: string;
  claimType: string;
  claimStatus: string;
  /**
   * Broker-side: may approve or reject a document. Mirrors the backend gate on
   * `verify`/`reject` (`IsBrokerEmployee`) — a policyholder approving their own
   * scan is not a check, which is why this is not simply "is signed in".
   */
  canReview: boolean;
  /** May attach and remove documents. Mirrors `IsBrokerEmployee | IsCustomer`. */
  canUpload: boolean;
}

function statusBadge(status: string): { variant: 'success' | 'error' | 'warning' | 'default'; label: string } {
  switch (status) {
    case 'approved':
      return { variant: 'success', label: 'Approved' };
    case 'rejected':
      return { variant: 'error', label: 'Rejected' };
    case 'resubmit':
      return { variant: 'error', label: 'Resubmission Required' };
    case 'pending':
      return { variant: 'warning', label: 'Awaiting Review' };
    default:
      return { variant: 'default', label: status || 'Unknown' };
  }
}

function ChecklistRow({ entry }: { entry: ChecklistEntry }) {
  const tone =
    entry.status === 'approved'
      ? { icon: CheckCircleIcon, className: 'text-green-500', text: 'Approved' }
      : entry.status === 'rejected'
        ? { icon: XCircleIcon, className: 'text-red-500', text: 'Rejected — upload a replacement' }
        : entry.status === 'uploaded'
          ? { icon: DocumentTextIcon, className: 'text-amber-500', text: 'Uploaded, awaiting review' }
          : { icon: ExclamationTriangleIcon, className: 'text-gray-400', text: 'Not uploaded yet' };
  const Icon = tone.icon;

  return (
    <li className="flex items-start gap-3 py-2">
      <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${tone.className}`} />
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white">{entry.label}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {tone.text}
          {entry.reason ? ` — ${entry.reason}` : ''}
        </p>
      </div>
    </li>
  );
}

/** DQ-346: the status chip on a served checklist row. */
function serverStatusTone(item: ServerChecklistItem) {
  switch (item.status) {
    case 'approved':
      return { icon: CheckCircleIcon, className: 'text-green-500', text: 'Approved' };
    case 'rejected':
      return { icon: XCircleIcon, className: 'text-red-500', text: 'Rejected — upload a replacement' };
    case 'resubmit':
      return { icon: XCircleIcon, className: 'text-red-500', text: 'Resubmit — upload a replacement' };
    case 'uploaded':
      return { icon: DocumentTextIcon, className: 'text-amber-500', text: 'Uploaded - awaiting review' };
    default:
      return { icon: ExclamationTriangleIcon, className: 'text-gray-400', text: 'Missing' };
  }
}

const REQUIREMENT_LABEL = { mandatory: 'Mandatory', optional: 'Optional', conditional: 'Conditional' } as const;

function ServerChecklistRow({
  item,
  canUpload,
  onUpload,
}: {
  item: ServerChecklistItem;
  canUpload: boolean;
  onUpload: (documentType: string) => void;
}) {
  const tone = serverStatusTone(item);
  const Icon = tone.icon;
  const greyed = item.requirement === 'conditional';
  const needsFile = item.status === 'missing' || item.status === 'rejected' || item.status === 'resubmit';
  return (
    <li className={`flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 py-2 ${greyed ? 'opacity-60' : ''}`}>
      <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${tone.className}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium text-gray-900 dark:text-white">{item.label}</p>
          <Badge size="sm" variant={item.requirement === 'mandatory' ? 'primary' : 'default'}>
            {REQUIREMENT_LABEL[item.requirement]}
          </Badge>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {tone.text}
          {item.rejection_reason ? ` — ${item.rejection_reason}` : ''}
        </p>
        {item.help_text && <p className="text-xs text-gray-400 dark:text-gray-500">{item.help_text}</p>}
        {greyed && item.condition_text && (
          <p className="text-xs text-gray-400 dark:text-gray-500">Needed: {item.condition_text}</p>
        )}
      </div>
      {canUpload && (
        <Button
          size="sm"
          variant={needsFile ? 'primary' : 'outline'}
          onClick={() => onUpload(item.document_type)}
          leftIcon={<DocumentArrowUpIcon className="w-4 h-4" />}
        >
          {item.status === 'missing' ? 'Upload' : needsFile ? 'Replace' : 'Add page'}
        </Button>
      )}
    </li>
  );
}

function ServerChecklistCard({
  checklist,
  canUpload,
  documentsPending,
  onUpload,
}: {
  checklist: ServerChecklist;
  canUpload: boolean;
  documentsPending: boolean;
  onUpload: (documentType: string) => void;
}) {
  return (
    <Card>
      <CardHeader
        title="Required Documents"
        subtitle={
          checklist.complete
            ? 'All mandatory documents uploaded'
            : `${checklist.mandatory_missing} mandatory document${checklist.mandatory_missing === 1 ? '' : 's'} still to upload`
        }
      />
      {checklist.complete && (
        <div className="mb-3 flex items-start gap-2 rounded-lg bg-green-50 dark:bg-green-900/20 p-3">
          <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
          <p className="text-sm text-green-800 dark:text-green-200">
            All mandatory documents uploaded. They are reviewed next; this is not a decision on the claim.
          </p>
        </div>
      )}
      {documentsPending && !checklist.complete && (
        <div className="mb-3 flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            This claim is waiting on documents. Upload the missing items below.
          </p>
        </div>
      )}
      {!checklist.upload_allowed && checklist.upload_blocked_reason && (
        <p className="mb-3 text-sm text-gray-600 dark:text-gray-300">{checklist.upload_blocked_reason}</p>
      )}
      <ul className="divide-y divide-gray-100 dark:divide-gray-700">
        {checklist.items.map((item) => (
          <ServerChecklistRow key={item.document_type} item={item} canUpload={canUpload} onUpload={onUpload} />
        ))}
      </ul>
      {checklist.other_uploaded.length > 0 && (
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Other uploaded: {checklist.other_uploaded.map((d) => d.name).join(', ')}
        </p>
      )}
      <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">{checklist.note}</p>
    </Card>
  );
}

export function ClaimDocumentsCard({
  claimId,
  claimType,
  claimStatus,
  canReview,
  canUpload,
}: ClaimDocumentsCardProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    data: documents,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['claim-documents', claimId],
    queryFn: () => claimDocumentsApi.list(claimId),
    enabled: !!claimId,
  });

  // DQ-346: cashless and reimbursement claims read their checklist from the
  // server; the client list below stays for the other claim types.
  const usesServerChecklist = SERVER_CHECKLIST_CLAIM_TYPES.includes(claimType);
  const { data: serverChecklist, error: checklistError } = useQuery({
    queryKey: ['claim-checklist', claimId],
    queryFn: () => claimChecklistApi.get(claimId),
    enabled: !!claimId && usesServerChecklist,
  });

  const rows = useMemo<ClaimDocumentRow[]>(() => documents ?? [], [documents]);
  const checklist = useMemo(
    () => (usesServerChecklist ? [] : buildChecklist(claimType, rows)),
    [claimType, rows, usesServerChecklist]
  );
  const outstanding = checklist.filter((entry) => entry.status !== 'approved');

  // A health claim follows the server's rule (a rejected claim stays open for
  // a short window); every other claim keeps the client list of finished statuses.
  const claimIsClosed = usesServerChecklist
    ? ['settled', 'closed', 'withdrawn'].includes(claimStatus) || serverChecklist?.upload_allowed === false
    : CLOSED_CLAIM_STATUSES.includes(claimStatus);
  const uploadAllowed = canUpload && !claimIsClosed;
  const documentsPending = claimStatus === DOCUMENTS_PENDING_STATUS;
  const documentTypeOptions = usesServerChecklist && serverChecklist
    ? [
        ...serverChecklist.items.map((item) => ({ value: item.document_type, label: item.label })),
        ...(serverChecklist.items.some((item) => item.document_type === 'other')
          ? []
          : [{ value: 'other', label: 'Other' }]),
      ]
    : CLAIM_DOCUMENT_TYPES;

  const [showUpload, setShowUpload] = useState(false);
  const [documentType, setDocumentType] = useState('');
  const [customName, setCustomName] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  /** Set when the upload modal was opened by "Replace" on an existing row. */
  const [replacing, setReplacing] = useState<ClaimDocumentRow | null>(null);

  const [rejecting, setRejecting] = useState<ClaimDocumentRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['claim-documents', claimId] });
    queryClient.invalidateQueries({ queryKey: ['claim-checklist', claimId] });
    queryClient.invalidateQueries({ queryKey: ['claim-timeline', claimId] });
    queryClient.invalidateQueries({ queryKey: ['claim', claimId] });
  };

  const closeUpload = () => {
    setShowUpload(false);
    setReplacing(null);
    setDocumentType('');
    setCustomName('');
    setFiles([]);
    setFileErrors([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openUpload = (replaceTarget?: ClaimDocumentRow, presetType?: string) => {
    setReplacing(replaceTarget ?? null);
    setDocumentType(replaceTarget?.document_type ?? presetType ?? '');
    setCustomName('');
    setFiles([]);
    setFileErrors([]);
    setShowUpload(true);
  };

  const handleFilesChosen = (event: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = Array.from(event.target.files ?? []);
    const accepted: File[] = [];
    const rejected: string[] = [];
    for (const file of chosen) {
      const problem = validateClaimDocumentFile(file);
      if (problem) rejected.push(problem);
      else accepted.push(file);
    }
    setFiles(accepted);
    setFileErrors(rejected);
  };

  /**
   * Upload every chosen file, then report what ACTUALLY happened.
   *
   * Sequential rather than `Promise.all` so a partial failure is legible: the
   * toast names how many landed and quotes the first real error, instead of a
   * blanket "Documents uploaded" over a batch where two of four 400'd. When a
   * replace was requested, the old row is only deleted once the new file is
   * safely stored — and if that delete then fails, the user is told both copies
   * are there rather than being left to discover it.
   */
  const uploadMutation = useMutation({
    mutationFn: async () => {
      const uploaded: ClaimDocumentRow[] = [];
      const failures: string[] = [];
      for (const file of files) {
        try {
          uploaded.push(
            await claimDocumentsApi.upload(claimId, {
              file,
              documentType,
              // A typed name only makes sense for a single file; with several,
              // each keeps its own filename so the list stays distinguishable.
              name: files.length === 1 ? customName : '',
            })
          );
        } catch (err) {
          failures.push(`${file.name}: ${getErrorMessage(err)}`);
        }
      }
      let replaceWarning = '';
      // DQ-346: on a health claim the replaced row stays as history; the
      // checklist follows the newest upload of the type.
      if (replacing && uploaded.length > 0 && !usesServerChecklist) {
        try {
          await claimDocumentsApi.remove(claimId, replacing.id);
        } catch (err) {
          replaceWarning = `The new file was stored, but the old "${replacing.name}" could not be removed: ${getErrorMessage(err)}`;
        }
      }
      return { uploaded, failures, replaceWarning };
    },
    onSuccess: ({ uploaded, failures, replaceWarning }) => {
      invalidate();
      if (uploaded.length > 0) {
        toast.success(
          uploaded.length === 1
            ? `${uploaded[0].name} uploaded`
            : `${uploaded.length} documents uploaded`
        );
      }
      if (replaceWarning) toast.error(replaceWarning);
      failures.forEach((message) => toast.error(message));
      if (failures.length === 0) closeUpload();
      else setFiles([]);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const verifyMutation = useMutation({
    mutationFn: (document: ClaimDocumentRow) => claimDocumentsApi.verify(claimId, document.id),
    onSuccess: (document) => {
      invalidate();
      toast.success(`${document.name} approved`);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ document, reason }: { document: ClaimDocumentRow; reason: string }) =>
      claimDocumentsApi.reject(claimId, document.id, reason),
    onSuccess: (document) => {
      invalidate();
      setRejecting(null);
      setRejectReason('');
      toast.success(`${document.name} sent back to the uploader`);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (document: ClaimDocumentRow) => claimDocumentsApi.remove(claimId, document.id),
    onSuccess: () => {
      invalidate();
      toast.success('Document deleted');
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const downloadMutation = useMutation({
    mutationFn: (document: ClaimDocumentRow) => claimDocumentsApi.download(claimId, document),
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const uploadDisabled = uploadMutation.isPending || files.length === 0 || !documentType;

  return (
    <>
      {/* Which required documents are still pending. Shown whenever the claim
          type has a checklist at all -- a policyholder should be able to see
          what is expected before the claim reaches "Documents Pending". */}
      {usesServerChecklist && checklistError && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 p-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">
            The document checklist could not be loaded: {getErrorMessage(checklistError)}
          </p>
        </div>
      )}
      {usesServerChecklist && serverChecklist && serverChecklist.items.length > 0 && (
        <ServerChecklistCard
          checklist={serverChecklist}
          canUpload={uploadAllowed}
          documentsPending={documentsPending}
          onUpload={(documentType) => openUpload(undefined, documentType)}
        />
      )}
      {checklist.length > 0 && (
        <Card>
          <CardHeader
            title="Required Documents"
            subtitle={
              outstanding.length === 0
                ? 'Every document on the standard checklist has been approved'
                : `${outstanding.length} of ${checklist.length} still to be approved`
            }
            action={
              uploadAllowed ? (
                <Button
                  size="sm"
                  onClick={() => openUpload()}
                  leftIcon={<DocumentArrowUpIcon className="w-4 h-4" />}
                >
                  Upload Document
                </Button>
              ) : undefined
            }
          />
          {documentsPending && outstanding.length > 0 && (
            <div className="mb-3 flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <p className="text-sm text-amber-800 dark:text-amber-200">
                This claim is waiting on documents. It stays at &ldquo;Documents Pending&rdquo; until the
                items below are uploaded and approved.
              </p>
            </div>
          )}
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {checklist.map((entry) => (
              <ChecklistRow key={entry.code} entry={entry} />
            ))}
          </ul>
          <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
            Standard checklist for a {titleCase(claimType).toLowerCase()} claim. Your insurer or
            surveyor may ask for more.
          </p>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Documents"
          subtitle={
            isLoading
              ? 'Loading…'
              : `${rows.length} ${rows.length === 1 ? 'document' : 'documents'} attached`
          }
          action={
            uploadAllowed && checklist.length === 0 ? (
              <Button
                size="sm"
                onClick={() => openUpload()}
                leftIcon={<DocumentArrowUpIcon className="w-4 h-4" />}
              >
                Upload Document
              </Button>
            ) : undefined
          }
        />

        {error ? (
          // Never an empty state over a failed request: "no documents" and "we
          // could not read the documents" are different facts.
          <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 p-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">
              The documents on this claim could not be loaded: {getErrorMessage(error)}
            </p>
          </div>
        ) : isLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading documents…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No documents uploaded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-4 font-medium">Document Name</th>
                  <th className="py-2 pr-4 font-medium">Document Type</th>
                  <th className="py-2 pr-4 font-medium">Uploaded</th>
                  <th className="py-2 pr-4 font-medium">Uploaded By</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map((doc) => {
                  const badge = statusBadge(doc.status);
                  return (
                    <tr key={doc.id} className="align-top">
                      <td className="py-3 pr-4">
                        <div className="flex items-start gap-2">
                          <DocumentTextIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 dark:text-white break-all">
                              {doc.name}
                            </p>
                            {formatFileSize(doc.file_size) && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {formatFileSize(doc.file_size)}
                              </p>
                            )}
                            {doc.rejection_reason && (
                              <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                                {doc.rejection_reason}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">
                        {documentTypeLabel(doc.document_type)}
                      </td>
                      <td className="py-3 pr-4 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {formatDateTime(doc.created_at)}
                      </td>
                      <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">
                        {doc.uploaded_by_name || '—'}
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant={badge.variant} size="sm">
                          {doc.status_display || badge.label}
                        </Badge>
                        {doc.verified_by_name && (
                          <p className="text-xs text-gray-400 mt-1">by {doc.verified_by_name}</p>
                        )}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadMutation.mutate(doc)}
                            isLoading={downloadMutation.isPending && downloadMutation.variables?.id === doc.id}
                            leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}
                          >
                            Download
                          </Button>
                          {canReview && doc.status !== 'approved' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => verifyMutation.mutate(doc)}
                              isLoading={verifyMutation.isPending && verifyMutation.variables?.id === doc.id}
                              className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
                            >
                              Verify
                            </Button>
                          )}
                          {canReview && doc.status !== 'rejected' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setRejecting(doc);
                                setRejectReason('');
                              }}
                              className="text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300"
                            >
                              Reject
                            </Button>
                          )}
                          {uploadAllowed && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openUpload(doc)}
                              leftIcon={<ArrowPathIcon className="w-4 h-4" />}
                            >
                              Replace
                            </Button>
                          )}
                          {uploadAllowed && (canReview || doc.status !== 'approved') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (window.confirm(`Delete document "${doc.name}"?`)) {
                                  deleteMutation.mutate(doc);
                                }
                              }}
                              className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        isOpen={showUpload}
        onClose={closeUpload}
        title={replacing ? `Replace "${replacing.name}"` : 'Upload Document'}
      >
        <div className="space-y-4">
          {replacing && !usesServerChecklist && (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              The corrected file is uploaded first; the original is removed only once it has been
              stored, so the claim is never left with neither.
            </p>
          )}
          {replacing && usesServerChecklist && (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              The new file is added to the claim; the earlier one stays on record.
            </p>
          )}
          {/* `name` is what `Select`/`Input` use as the element id, and the id is
              what ties the <label> to the control -- without it the field has a
              visible label and no accessible name. */}
          <Select
            name="claim_document_type"
            label="Document Type"
            options={[{ value: '', label: 'Select the document type' }, ...documentTypeOptions]}
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
          />
          <div>
            <label
              htmlFor="claim_document_files"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
            >
              Files
            </label>
            <input
              id="claim_document_files"
              ref={fileInputRef}
              type="file"
              multiple
              accept={UPLOAD_ACCEPT_ATTRIBUTE}
              onChange={handleFilesChosen}
              className="block w-full text-sm text-gray-700 dark:text-gray-300 file:mr-3 file:rounded-lg file:border-0 file:bg-primary-50 dark:file:bg-primary-900/20 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-700 dark:file:text-primary-300 hover:file:bg-primary-100 dark:hover:file:bg-primary-900/30"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              PDF, JPG, PNG, WEBP, HEIC, TIFF, Word or Excel. Up to 10 MB each. You can pick several
              at once.
            </p>
          </div>
          {files.length === 1 && (
            <Input
              name="claim_document_name"
              label="Name (optional)"
              placeholder={files[0].name}
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
            />
          )}
          {files.length > 0 && (
            <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
              {files.map((file) => (
                <li key={file.name} className="flex items-center gap-2">
                  <DocumentTextIcon className="w-4 h-4 text-gray-400" />
                  <span className="break-all">{file.name}</span>
                  <span className="text-xs text-gray-400">{formatFileSize(file.size)}</span>
                </li>
              ))}
            </ul>
          )}
          {fileErrors.length > 0 && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-3 space-y-1">
              {fileErrors.map((message) => (
                <p key={message} className="text-sm text-red-700 dark:text-red-300">
                  {message}
                </p>
              ))}
            </div>
          )}
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={closeUpload}>
            Cancel
          </Button>
          <Button
            onClick={() => uploadMutation.mutate()}
            isLoading={uploadMutation.isPending}
            disabled={uploadDisabled}
          >
            {files.length > 1 ? `Upload ${files.length} Documents` : 'Upload'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={!!rejecting}
        onClose={() => {
          setRejecting(null);
          setRejectReason('');
        }}
        title="Reject Document"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {rejecting?.name}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              rows={3}
              maxLength={255}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="What is wrong with this document? The uploader sees this."
            />
          </div>
        </div>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              setRejecting(null);
              setRejectReason('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              rejecting && rejectMutation.mutate({ document: rejecting, reason: rejectReason.trim() })
            }
            isLoading={rejectMutation.isPending}
            disabled={!rejectReason.trim()}
          >
            Reject Document
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
